import {useRef, useState} from 'react';
import {getArea, getIntersectionArea} from 'ol/extent';
import {transform, transformExtent} from 'ol/proj';
import distance from '@turf/distance';

import {EPSG3857, EPSG4326} from 'topmeteo/flight-planning/utils/projection';

import {useMap} from '../OpenLayers/hooks';

import WeatherImageController from './WeatherImageController';

const Properties = ['center', 'resolution'];
const Events = Properties.map(property => `change:${property}`);

const onNextFrame = window.requestAnimationFrame || (callback => setTimeout(callback, 0));
const cancelOnNextFrame = window.cancelAnimationFrame || clearTimeout;

const selectRegion = (regions, view) => {
    const candidates = regions
        .map(region => ({
            ...region,
            coverage: getIntersectionArea(view.extent, region.extent) / view.area,
        }))
        .filter(({coverage}) => coverage)
        .map(region => ({
            ...region,
            isCovered: region.coverage > 0.8,
            distance: distance(view.center, region.center),
        }))
        .sort((left, right) => {
            const coverageDifference = left.isCovered && right.isCovered ? 0 : right.coverage - left.coverage;

            if (coverageDifference) return coverageDifference;
            if (left.depth !== right.depth) return right.depth - left.depth;
            if (left.distance !== right.distance) return left.distance - right.distance;

            return 0;
        });

    return candidates.length ? candidates[0] : null;
};

const WeatherImageSelector = ({regions, zIndex}) => {
    const timerRef = useRef(0);
    const [region, setRegion] = useState(null);

    useMap(
        map => {
            const size = map.getSize();
            const view = map.getView();

            const updateRegion = () => {
                const center = transform(view.getCenter(), EPSG3857, EPSG4326);
                const extent = transformExtent(view.calculateExtent(size), EPSG3857, EPSG4326);
                const region = selectRegion(regions, {area: getArea(extent), center, extent});

                setRegion(region);
            };

            const handleChange = () => {
                cancelOnNextFrame(timerRef.current);
                onNextFrame(updateRegion);
            };

            view.on(Events, handleChange);

            updateRegion();

            return () => view.un(Events, handleChange);
        },
        [regions, timerRef],
    );

    return region && <WeatherImageController region={region} zIndex={zIndex} />;
};

export default WeatherImageSelector;
